/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import { FormControl, Grid, MenuItem, TextField, Typography, useMediaQuery, useTheme } from '@material-ui/core';
import { Controller } from 'react-hook-form';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import Errors from '../../Errors';
import Tooltip from '../../../common/Tooltip';
import { useAttachmentsConfig } from '../hooks/useAttachmentsConfig';
import {
  allowedAddFileFormats,
  ATTACHMENT_CONFIG_ATTRIBUTES,
  maxCombinedFilesSize,
  specialDocumentTypes,
} from '../services/constants';
import {
  filterAttachmentConfigByType,
  getAttachmentConfigValue,
  getDisabledAttachmentTypes,
  getUnsupportedAttachmentTypes,
  isAttachmentTypeUploaded,
  isCombinedAttachmentPossible,
} from '../services/attachments.helper';
import { uiSchemaProperties } from '../../../../shared/enums/uiSchemaProperties';
import { useAttachmentMetaFields } from './meta/useAttachmentMetaFields';
import { trimToSize } from '../../../../shared/utils/stringUtils';
import { useConfiguration } from '../../../../redux/features/configuration/useConfiguration';
import { ConfigResourceProps } from '../../../../redux/enums/ConfigResourceProps';
import useProcedure from '../../../../procedure/useProcedure';
/**
 * Selector for the attachment subtype hosted within the attachment uploader component.
 * @param attachmentType - hosting attachment type
 * @param attachmentsUiSchema - uiSchema attachments configuration
 * @param uploadedAttachmentsMetadata - submission model attachment data for the given attachment type
 * @param allAttachmentsMetadata - uploaded and dropped attachment data
 * @param metaForm - use form hook methods
 * @param typeLabel - subtype selector label
 * @param hasError - indicator flag for field error
 * @param rjsfFormClasses - RJSF classes
 * @param uploaderClasses - hosting uploader classes
 * @returns {false|JSX.Element}
 * @constructor
 */
const AttachmentSubTypeSelector = ({
  attachmentType,
  attachmentsUiSchema,
  uploadedAttachmentsMetadata,
  allAttachmentsMetadata,
  metaForm,
  typeLabel,
  hasError,
  rjsfFormClasses,
  uploaderClasses,
  jsonSchema,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMediumOrBigger = useMediaQuery(theme.breakpoints.up('md'));
  const ATTACHMENT_LABEL_MAX_LENGTH = 80;
  const { attachmentTypesConfig } = useAttachmentsConfig();
  const { extractDropdownDefinition } = useAttachmentMetaFields();
  const { errors, control } = metaForm;
  const showComplianceInfo = attachmentsUiSchema[uiSchemaProperties.ATTACHMENTS_SHOW_COMPLIANCE_INFO];
  const showAttachmentTooltips = attachmentsUiSchema[uiSchemaProperties.ATTACHMENTS_SHOW_TOOLTIPS];
  const allowedFileFormats = attachmentsUiSchema[uiSchemaProperties.ALLOWED_FILE_FORMATS];
  const useDefaultValue = attachmentsUiSchema[uiSchemaProperties.ATTACHMENTS_USE_DEFAULT_VALUE] !== false;
  const frontendCustomisation = useConfiguration(ConfigResourceProps.FRONTEND_CUSTOMISATION);
  const procedure = useProcedure();

  const getMenuItems = (attachmentType_) => {
    const excludedTypes = getUnsupportedAttachmentTypes(attachmentsUiSchema);
    const attachmentConfigs = filterAttachmentConfigByType(attachmentTypesConfig, attachmentType_, excludedTypes);

    const disabledAttachmentTypes = getDisabledAttachmentTypes(attachmentsUiSchema);

    return attachmentConfigs?.map((config) => {
      const label = t(`attachments.type.${config.type}`);

      const maxOneAttachmentPerSubtype = getAttachmentConfigValue(
        config,
        ATTACHMENT_CONFIG_ATTRIBUTES.MAX_ONE_ATTACHMENT_PER_SUBTYPE,
      );

      const needDistinctTypes = getAttachmentConfigValue(config, ATTACHMENT_CONFIG_ATTRIBUTES.NEED_DISTINCT_TYPES);
      const dropdownDefinition = extractDropdownDefinition(config, jsonSchema);

      let disabled =
        disabledAttachmentTypes.includes(config.type) ||
        (isAttachmentTypeUploaded(uploadedAttachmentsMetadata, config.type, dropdownDefinition) &&
          (maxOneAttachmentPerSubtype || needDistinctTypes));

      // TODO: add the check for the distinct type

      if (config.type === specialDocumentTypes.COMBINED) {
        disabled =
          disabled || !isCombinedAttachmentPossible(allAttachmentsMetadata, attachmentTypesConfig, excludedTypes);
      }

      return (
        <MenuItem key={config.type} value={config.type} disabled={disabled}>
          <span title={label}>{trimToSize(label, ATTACHMENT_LABEL_MAX_LENGTH)}</span>
        </MenuItem>
      );
    });
  };

  const getTooltip = (attachmentType_) => {
    const _allowedAddFileFormats = allowedFileFormats
      ? allowedFileFormats.join(' - ')
      : allowedAddFileFormats.join(' - ');
    const prodecure = procedure.id.toLowerCase();
    const attachmentType__ = attachmentType_.toLowerCase();
    const override = frontendCustomisation?.resource?.attachments_tooltip_by_procedure;
    return (
      <Typography variant="subtitle1" component="span">
        {override && ['TECHNICAL', 'ADDITIONAL'].indexOf(attachmentType_) > -1 ? (
          <p>
            <Trans
              components={{
                br: <br />,
              }}
              i18nKey={t(`attachments.tooltip.${prodecure}.${attachmentType__}`)}
            />
          </p>
        ) : (
          <p>
            <Trans
              components={{
                br: <br />,
              }}
              i18nKey={t(`attachments.tooltip.${attachmentType__}`)}
            />
          </p>
        )}
        <p>
          <Trans
            components={{
              br: <br />,
            }}
          >
            {t('attachments.type.supported')}
            {_allowedAddFileFormats}
          </Trans>
        </p>
        <p>
          <Trans
            components={{
              br: <br />,
            }}
          >
            {t('attachments.total.size.allowed')}
            {maxCombinedFilesSize}
          </Trans>
        </p>
      </Typography>
    );
  };

  const selectorItems = getMenuItems(attachmentType);
  const defaultValue = selectorItems.length === 1 && useDefaultValue ? selectorItems[0].key : '';

  return (
    !defaultValue && (
      <Grid container direction="row" alignItems="center" className={uploaderClasses.formGroup}>
        <FormControl variant="outlined">
          <Controller
            className={rjsfFormClasses.textField}
            name="attachmentSubType"
            id={attachmentType}
            data-testid={attachmentType}
            error={hasError}
            variant="outlined"
            select
            SelectProps={{
              MenuProps: {
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left',
                },
                getContentAnchorEl: null,
                PaperProps: {
                  style: {
                    maxHeight: '20rem',
                  },
                },
              },
            }}
            label={isMediumOrBigger ? typeLabel : t('select.option')}
            as={<TextField>{selectorItems}</TextField>}
            defaultValue={defaultValue}
            control={control}
            rules={{ required: t('field.is.required') }}
          />
          <Errors errors={errors} property={attachmentType} classes={rjsfFormClasses} />
        </FormControl>
        {(!showComplianceInfo || showAttachmentTooltips) && (
          <Tooltip placement="top" title={getTooltip(attachmentType)} />
        )}
      </Grid>
    )
  );
};

export default AttachmentSubTypeSelector;
